// frontend/src/components/newui/NewReadyCheckModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Copy, Clock, Check, ExternalLink } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../config/axios';
import NewUserAvatar from './NewUserAvatar';

const PlayerStatus = ({ username = 'Sin nombre', isReady, image }) => {
    const userForAvatar = {
        username,
        image,
        name: username
    };

    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
            <div className="flex items-center space-x-3">
                <NewUserAvatar user={userForAvatar} size="md" />
                <span className="font-semibold text-[#122436]">{username}</span>
            </div>
            {isReady ? (
                <span className="text-green-500 flex items-center gap-1 font-bold">
                    <Check className="w-5 h-5" />
                    Listo
                </span>
            ) : (
                <span className="text-gray-500 font-semibold">Esperando...</span>
            )}
        </div>
    );
};

const NewReadyCheckModal = ({ match, onClose }) => {
    const { user } = useAuth();
    const [timeLeft, setTimeLeft] = useState(null);
    const [readyState, setReadyState] = useState({
        player1Ready: false,
        player2Ready: false
    });
    const [matchUrl, setMatchUrl] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const { socket } = useSocket();
    const [isClosing, setIsClosing] = useState(false);
    const [timerInterval, setTimerInterval] = useState(null);

    const amIPlayer1 = user.id === match.player1Id;

    useEffect(() => {
        if (match) {
            setReadyState({
                player1Ready: match.player1Ready || false,
                player2Ready: match.player2Ready || false
            });

            if (match.status === 'IN_PROGRESS' && (match.gameUrl || match.gameInstance)) {
                setMatchUrl(match.gameUrl || `${window.location.origin}/games/truco/game/${match.id}`);
            }
        }
    }, [match]);

    const startLocalTimer = (initialTime) => {
        if (timerInterval) {
            clearInterval(timerInterval);
        }

        setTimeLeft(initialTime);

        const interval = setInterval(() => {
            setTimeLeft(current => {
                if (current <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return current - 1;
            });
        }, 1000);

        setTimerInterval(interval);
    };

    useEffect(() => {
        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [timerInterval]);

    useEffect(() => {
        if (!socket) return;

        const handleReadyUpdate = (data) => {
            console.log("Recibida actualización del match:", data);

            if (data.matchId === match.id) {
                setReadyState({
                    player1Ready: data.player1Ready || false,
                    player2Ready: data.player2Ready || false
                });

                if (data.timeRemaining !== undefined) {
                    startLocalTimer(data.timeRemaining);
                }

                if (data.gameUrl) {
                    setMatchUrl(data.gameUrl);
                } else if (data.matchUrl) {
                    setMatchUrl(data.matchUrl);
                }
            }
        };

        socket.on('match_ready_update', handleReadyUpdate);
        socket.emit('match_ready_status', { matchId: match.id });

        return () => {
            socket.off('match_ready_update', handleReadyUpdate);
        };
    }, [socket, match.id]);

    const handleReady = async () => {
        try {
            const response = await axios.post(`/matches/${match.id}/ready`);

            if (response.data.success) {
                if (response.data.timeRemaining !== undefined) {
                    startLocalTimer(response.data.timeRemaining);
                }

                if (response.data.match.gameUrl) {
                    setMatchUrl(response.data.match.gameUrl);
                }

                setReadyState(prevState => ({
                    ...prevState,
                    player1Ready: amIPlayer1 ? true : prevState.player1Ready,
                    player2Ready: !amIPlayer1 ? true : prevState.player2Ready
                }));
            }
        } catch (error) {
            console.error('Error al marcar como listo:', error);
        }
    };

    const handleClose = async () => {
        setIsClosing(true);
        try {
            const myReadyState = amIPlayer1 ? readyState.player1Ready : readyState.player2Ready;
            if (myReadyState && !bothReady) {
                await axios.post(`/matches/${match.id}/cancel-ready`);
            }

            if (timerInterval) {
                clearInterval(timerInterval);
            }

            onClose();
        } catch (error) {
            console.error('Error al cancelar ready state:', error);
            onClose();
        }
    };

    const copyMatchUrl = () => {
        if (!matchUrl) return;
        navigator.clipboard.writeText(matchUrl);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const formatTime = (seconds) => {
        if (seconds === null) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const myReadyState = amIPlayer1 ? readyState.player1Ready : readyState.player2Ready;
    const opponentReadyState = amIPlayer1 ? readyState.player2Ready : readyState.player1Ready;
    const bothReady = readyState.player1Ready && readyState.player2Ready;
    const showTimer = (readyState.player1Ready || readyState.player2Ready) && !bothReady;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full relative shadow-2xl">
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-6 h-6"/>
                </button>

                <h2 className="toon text-3xl text-[#122436] mb-6 uppercase">Preparando el Match</h2>

                {/* Mensaje de estado */}
                {showTimer && (
                    <div className="mb-4 p-3 bg-blue-50 border-2 border-[#2284DF] rounded-xl">
                        <p className="text-sm text-[#122436] font-semibold">
                            {myReadyState && !opponentReadyState ?
                                "Esperando al oponente. Si el tiempo se acaba sin que se prepare, ganarás automáticamente." :
                                "Tu oponente está listo. Presiona 'Estoy Listo' antes de que se acabe el tiempo."
                            }
                        </p>
                    </div>
                )}

                {/* Temporizador */}
                {showTimer && (
                    <div className="flex items-center justify-center mb-6">
                        <div className={`flex items-center gap-2 text-2xl font-extrabold 
                            ${timeLeft !== null && timeLeft < 60 ? 'text-red-500' : 'text-[#2284DF]'}`}>
                            <Clock className="w-7 h-7" />
                            {formatTime(timeLeft)}
                        </div>
                    </div>
                )}

                {/* Estado de los jugadores */}
                <div className="space-y-3 mb-6">
                    <PlayerStatus
                        username={match.player1Name}
                        isReady={readyState.player1Ready}
                        image={match.player1Image}
                    />
                    <PlayerStatus
                        username={match.player2Name}
                        isReady={readyState.player2Ready}
                        image={match.player2Image}
                    />
                </div>

                {/* URL del match y botón de copiar */}
                {matchUrl && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border-2 border-gray-200">
                            <span className="text-sm text-gray-600 truncate mr-2 font-mono">
                                {matchUrl}
                            </span>
                            <button
                                onClick={copyMatchUrl}
                                className="text-[#2284DF] hover:text-[#2A91F4] transition-colors flex-shrink-0"
                                title="Copiar URL"
                            >
                                <Copy className="w-5 h-5" />
                            </button>
                        </div>
                        {isCopied && (
                            <p className="text-sm text-green-600 mt-2 font-semibold">¡URL copiada!</p>
                        )}
                    </div>
                )}

                {/* Botón de acción */}
                {bothReady && matchUrl ? (
                    <a
                        href={matchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-12 md:h-14 bg-green-600 text-white rounded-xl font-extrabold uppercase tracking-wide
                                 hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <ExternalLink className="w-5 h-5" />
                        Iniciar Match
                    </a>
                ) : (
                    <button
                        onClick={handleReady}
                        disabled={myReadyState}
                        className="w-full h-12 md:h-14 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                 hover:bg-[#2A91F4] transition-colors disabled:opacity-50 
                                 disabled:cursor-not-allowed flex items-center justify-center gap-2
                                 border-2 border-[#1E75C6]"
                    >
                        {myReadyState ? (
                            <>
                                <Check className="w-5 h-5" />
                                Esperando Oponente
                            </>
                        ) : (
                            'Estoy Listo'
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default NewReadyCheckModal;